import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { UserPlus, Search, Users, GraduationCap, Briefcase } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    'Authorization': token ? `Bearer ${token}` : ''
  };
};

const AddMember = () => {
  const [loading, setLoading] = useState(false);
  const [students, setStudents] = useState([]);
  const [staff, setStaff] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [memberType, setMemberType] = useState('Student');
  const [selectedUser, setSelectedUser] = useState(null);
  const [maxBooks, setMaxBooks] = useState(3);
  const [existingMembers, setExistingMembers] = useState([]);

  useEffect(() => {
    fetchStudents();
    fetchStaff();
    fetchExistingMembers();
  }, []);

  const fetchStudents = async () => {
    try {
      // Use the lightweight `dropdown` endpoint which returns up to 5000 students
      // (vs the paginated `/api/students/` default of 25 that was clipping the list).
      const response = await axios.get(`${API_URL}/api/students/dropdown`, { headers: getAuthHeaders() });
      const data = Array.isArray(response.data) ? response.data : [];
      console.log('Students fetched:', data.length);
      setStudents(data);
    } catch (error) {
      console.error('Error fetching students:', error);
      setStudents([]);
    }
  };

  const fetchStaff = async () => {
    try {
      // Correct endpoint: /api/staff/ (with trailing slash)
      const response = await axios.get(`${API_URL}/api/staff/`, { headers: getAuthHeaders() });
      const data = Array.isArray(response.data) ? response.data : [];
      console.log('Staff fetched:', data.length);
      setStaff(data);
    } catch (error) {
      console.error('Error fetching staff:', error);
      setStaff([]);
    }
  };

  const fetchExistingMembers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/library/members`, { headers: getAuthHeaders() });
      const data = Array.isArray(response.data) ? response.data : [];
      setExistingMembers(data.map(m => m.user_id));
    } catch (error) {
      console.error('Error fetching members:', error);
      setExistingMembers([]);
    }
  };

  const generateCardNumber = () => {
    const prefix = memberType === 'Student' ? 'LIB-STD' : 'LIB-STF';
    const randomNum = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
    return `${prefix}-${new Date().getFullYear()}-${randomNum}`;
  };

  const handleAddMember = async () => {
    if (!selectedUser) {
      alert('Please select a student or staff member');
      return;
    }

    // Check if already a member
    if (existingMembers.includes(selectedUser.id)) {
      alert('This person is already a library member!');
      return;
    }

    setLoading(true);

    try {
      const memberData = {
        user_id: selectedUser.id,
        user_name: selectedUser.full_name,
        user_type: memberType,
        member_card_number: generateCardNumber(),
        max_books_allowed: maxBooks,
        email: selectedUser.email || '',
        phone: selectedUser.phone || selectedUser.mobile || '',
        course_name: memberType === 'Student' ? (selectedUser.course_name || '') : '',
        department: memberType === 'Staff' ? (selectedUser.department || '') : ''
      };

      await axios.post(`${API_URL}/api/library/members`, memberData, { headers: getAuthHeaders() });
      alert('Library member added successfully!');
      setSelectedUser(null);
      setSearchTerm('');
      fetchExistingMembers(); // Refresh existing members list
    } catch (error) {
      console.error('Error adding member:', error);
      alert(error.response?.data?.detail || 'Failed to add member');
    } finally {
      setLoading(false);
    }
  };

  const userList = memberType === 'Student' ? students : staff;
  
  // Filter users based on search and exclude existing members
  const filteredUsers = userList.filter(user => {
    const matchesSearch = 
      user.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.roll_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.employee_id?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const notAlreadyMember = !existingMembers.includes(user.id);
    
    return matchesSearch && notAlreadyMember;
  });

  return (
    <div className="p-6">
      <Card className="max-w-3xl mx-auto">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserPlus className="w-5 h-5" />
            Add Library Member
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Member Type Selection */}
          <div>
            <label className="block text-sm font-medium mb-2">Member Type</label>
            <div className="flex gap-4">
              <Button
                type="button"
                variant={memberType === 'Student' ? 'default' : 'outline'}
                onClick={() => {
                  setMemberType('Student');
                  setSelectedUser(null);
                  setSearchTerm('');
                }}
                className="flex-1 gap-2"
              >
                <GraduationCap className="w-4 h-4" />
                Student ({students.length})
              </Button>
              <Button
                type="button"
                variant={memberType === 'Staff' ? 'default' : 'outline'}
                onClick={() => {
                  setMemberType('Staff');
                  setSelectedUser(null);
                  setSearchTerm('');
                }}
                className="flex-1 gap-2"
              >
                <Briefcase className="w-4 h-4" />
                Staff ({staff.length})
              </Button>
            </div>
          </div>

          {/* Search Section */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Search & Select {memberType} <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder={`Search by name${memberType === 'Student' ? ', roll number' : ', employee ID'}...`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* User List - Show all if no search or filtered if searching */}
            <div className="mt-2 max-h-60 overflow-y-auto border rounded-lg">
              {(searchTerm ? filteredUsers : userList.filter(u => !existingMembers.includes(u.id))).length === 0 ? (
                <p className="p-4 text-gray-500 text-center">
                  {userList.length === 0 
                    ? `No ${memberType.toLowerCase()}s found in system` 
                    : `All ${memberType.toLowerCase()}s are already library members`}
                </p>
              ) : (
                (searchTerm ? filteredUsers : userList.filter(u => !existingMembers.includes(u.id))).map((user) => (
                  <div
                    key={user.id}
                    onClick={() => setSelectedUser(user)}
                    className={`p-3 border-b cursor-pointer hover:bg-gray-50 transition-colors ${
                      selectedUser?.id === user.id ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${
                        memberType === 'Student' ? 'bg-blue-500' : 'bg-green-500'
                      }`}>
                        {user.full_name?.charAt(0) || '?'}
                      </div>
                      <div>
                        <div className="font-medium">{user.full_name}</div>
                        <div className="text-sm text-gray-600">
                          {memberType === 'Student' 
                            ? `${user.course_name || 'No Course'} ${user.roll_number ? `| Roll: ${user.roll_number}` : ''}`
                            : `${user.designation || 'Staff'} ${user.employee_id ? `| ID: ${user.employee_id}` : ''}`
                          }
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Selected User Display */}
            {selectedUser && (
              <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg ${
                    memberType === 'Student' ? 'bg-blue-500' : 'bg-green-500'
                  }`}>
                    {selectedUser.full_name?.charAt(0) || '?'}
                  </div>
                  <div>
                    <h3 className="font-semibold text-green-900">{selectedUser.full_name}</h3>
                    <p className="text-sm text-green-700">
                      {memberType === 'Student'
                        ? `${selectedUser.course_name || 'No Course'}`
                        : `${selectedUser.designation || 'Staff'}`
                      }
                    </p>
                    {selectedUser.email && (
                      <p className="text-xs text-green-600">{selectedUser.email}</p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Max Books Allowed */}
          <div>
            <label className="block text-sm font-medium mb-2">Maximum Books Allowed</label>
            <Input
              type="number"
              min="1"
              max="10"
              value={maxBooks}
              onChange={(e) => setMaxBooks(parseInt(e.target.value) || 3)}
              className="max-w-xs"
            />
            <p className="text-sm text-gray-500 mt-1">
              Number of books this member can borrow at a time
            </p>
          </div>

          {/* Summary */}
          {selectedUser && (
            <div className="p-4 bg-gray-50 rounded-lg">
              <h4 className="font-medium mb-2">Summary</h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="text-gray-600">Name:</div>
                <div>{selectedUser.full_name}</div>
                <div className="text-gray-600">Type:</div>
                <div>{memberType}</div>
                <div className="text-gray-600">Max Books:</div>
                <div>{maxBooks}</div>
                <div className="text-gray-600">Card Number:</div>
                <div className="text-blue-600 font-mono">Auto-generated on save</div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <Button
              onClick={handleAddMember}
              disabled={!selectedUser || loading}
              className="flex-1"
            >
              {loading ? 'Adding...' : 'Add as Library Member'}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setSelectedUser(null);
                setSearchTerm('');
              }}
            >
              Clear
            </Button>
          </div>

          {/* Info Box */}
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start gap-2">
              <Users className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <h4 className="font-medium text-blue-900">How it works</h4>
                <ul className="text-sm text-blue-800 mt-1 space-y-1">
                  <li>• Select Student or Staff member type</li>
                  <li>• Click on a person from the list to select</li>
                  <li>• A unique library card number will be auto-generated</li>
                  <li>• Already registered members are hidden from the list</li>
                </ul>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AddMember;
